import { useQuery } from '@tanstack/react-query';
import { parseApiSource } from '@interxfi/j5-parse/parse-source';
import type { APISource } from '@interxfi/j5-parse/types/api';
import type { ParsedSource } from '@interxfi/j5-parse/types/parsed';
import { useRealmAuthIsAuthenticated } from '../context';
import { getRegistryBaseUrl } from './registry';

export type ApiSourceBundle = 'api' | 'program-api' | 'clearing-api' | 'techops-api'; // where 'api' is the full bundle

const REGISTRY_PREFIX = 'registry/v1';
const API_DOT_JSON_PATH = 'api.json';

function getAPISourceURL(version: string = 'latest', bundle: ApiSourceBundle = 'program-api') {
  return `${getRegistryBaseUrl()}/${REGISTRY_PREFIX}/interxfi/${bundle}/${version}/${API_DOT_JSON_PATH}`;
}

const REGISTRY_API_JSON_KEY = ['registry/api.json'] as const;

function getAPISourceKey(version: string = 'latest', bundle: ApiSourceBundle) {
  return [...REGISTRY_API_JSON_KEY, bundle, version] as const;
}

export async function fetchApiSource(version: string = 'latest', bundle: ApiSourceBundle = 'program-api') {
  const response = await fetch(getAPISourceURL(version, bundle));
  const json = await response.json();

  try {
    const apiSource = parseApiSource(json as APISource, response.headers.get('x-version') || undefined);

    const packages = bundle === 'api' ? apiSource.packages : apiSource.packages.filter((pkg) => !pkg.hidden);

    return {
      ...apiSource,
      packages,
    } as const satisfies ParsedSource;
  } catch {
    return null;
  }
}

export function getApiSourceQueryOptions(version: string = 'latest', bundle: ApiSourceBundle = 'program-api') {
  return {
    queryKey: getAPISourceKey(version, bundle),
    queryFn: () => fetchApiSource(version, bundle),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
  } as const;
}

export function useApiDefinitions(version: string = 'latest', bundle: ApiSourceBundle = 'program-api') {
  const isAuthenticated = useRealmAuthIsAuthenticated();

  return useQuery({
    ...getApiSourceQueryOptions(version, bundle),
    enabled: isAuthenticated,
  });
}
